import { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { Navbar } from "@/components/Navbar";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { api, formatApiError, formatINR } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { ArrowLeft, ArrowRight, Printer, IndianRupee, CheckCircle2, Truck, User, PackageCheck, AlertTriangle, XCircle, Pencil, Trash2 } from "lucide-react";
import { StatusBadge } from "@/components/StatusBadge";

const NEXT_STATUS = {
  approved: "loaded", loaded: "in_transit", in_transit: "delivered", delivered: "closed",
};

export default function TripDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [trip, setTrip] = useState(null);
  const [expenses, setExpenses] = useState([]);
  const [payments, setPayments] = useState([]);
  const [exp, setExp] = useState({ kind: "fuel", amount: "", note: "" });
  const [pay, setPay] = useState({ kind: "lorry_advance", amount: "", method: "bank", note: "" });
  const [pod, setPod] = useState({ outcome: "delivered_ok", remarks: "", shortage_qty: "", damage_note: "" });
  const [editOpen, setEditOpen] = useState(false);
  const [delOpen, setDelOpen] = useState(false);
  const [edit, setEdit] = useState({});

  const load = async () => {
    try {
      const [{ data: t }, { data: e }, { data: p }] = await Promise.all([
        api.get(`/trips/${id}`),
        api.get(`/trips/${id}/expenses`),
        api.get(`/trips/${id}/payments`),
      ]);
      setTrip(t.trip); setExpenses(e.expenses); setPayments(p.payments);
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };
  useEffect(() => { load(); }, [id]);

  const approve = async () => {
    try { await api.post(`/trips/${id}/approve`); toast.success("Trip approved"); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const advance = async (nextStatus) => {
    try { await api.post(`/trips/${id}/status`, { status: nextStatus }); toast.success(`Marked ${nextStatus.replace("_", " ")}`); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const addExpense = async (e) => {
    e.preventDefault();
    try {
      await api.post(`/trips/${id}/expenses`, { ...exp, amount: Number(exp.amount) });
      toast.success("Expense added"); setExp({ kind: "fuel", amount: "", note: "" }); load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };
  const addPayment = async (e) => {
    e.preventDefault();
    try {
      await api.post(`/trips/${id}/payments`, { ...pay, amount: Number(pay.amount) });
      toast.success("Payment recorded"); setPay({ ...pay, amount: "", note: "" }); load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };
  const savePod = async (e) => {
    e.preventDefault();
    try {
      await api.post(`/trips/${id}/pod`, {
        outcome: pod.outcome,
        remarks: pod.remarks,
        shortage_qty: Number(pod.shortage_qty || 0),
        damage_note: pod.damage_note,
      });
      toast.success("POD recorded"); load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const openEdit = () => {
    setEdit({
      customer_name: trip.customer?.name || "",
      customer_phone: trip.customer?.phone || "",
      customer_gstin: trip.customer?.gstin || "",
      consignor: trip.customer?.consignor || "",
      consignee: trip.customer?.consignee || "",
      source_city: trip.source_city || "",
      destination_city: trip.destination_city || "",
      material: trip.material || "",
      weight_kg: trip.weight_kg || 0,
      invoice_number: trip.invoice_number || "",
      sales_order_number: trip.sales_order_number || "",
      pickup_date: trip.pickup_date || "",
      delivery_date: trip.delivery_date || "",
      customer_freight: trip.money?.customer_freight || 0,
      lorry_hire_total: trip.money?.lorry_hire_total || 0,
      lorry_advance: trip.money?.lorry_advance || 0,
      driver_hire_total: trip.money?.driver_hire_total || 0,
      remarks: trip.remarks || "",
    });
    setEditOpen(true);
  };

  const saveEdit = async () => {
    try {
      const payload = { ...edit };
      ["weight_kg", "customer_freight", "lorry_hire_total", "lorry_advance", "driver_hire_total"].forEach(k => {
        payload[k] = Number(payload[k] || 0);
      });
      await api.patch(`/trips/${id}`, payload);
      toast.success("Trip updated");
      setEditOpen(false);
      load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const confirmDelete = async () => {
    try {
      await api.delete(`/trips/${id}`);
      toast.success("Trip deleted");
      setDelOpen(false);
      navigate(user?.role === "super_admin" ? "/superadmin" : "/ops");
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  if (!trip) return <div className="min-h-screen bg-[#0A0A0A] text-white p-10">Loading…</div>;

  const totalExpense = expenses.reduce((a, e) => a + Number(e.amount || 0), 0);
  const canApprove = trip.status === "draft" && ["super_admin", "admin"].includes(user?.role);
  const nextStatus = NEXT_STATUS[trip.status];
  const canAdvance = nextStatus && !["draft", "cancelled"].includes(trip.status);
  const isOps = ["loading_supervisor", "accounts", "super_admin", "admin"].includes(user?.role);
  const isAcc = ["accounts", "super_admin", "admin"].includes(user?.role);
  const canRecordPod = isOps && ["in_transit", "delivered", "closed"].includes(trip.status);
  const canEdit = isOps && trip.status !== "closed" && !trip.billed;
  const canDelete = ["super_admin", "admin"].includes(user?.role) && !trip.billed;
  const podOutcomeMeta = {
    delivered_ok:     { label: "Delivered OK",       color: "emerald", Icon: PackageCheck },
    short_delivery:   { label: "Short Delivery",     color: "amber",   Icon: AlertTriangle },
    damaged_delivery: { label: "Damaged Delivery",   color: "rose",    Icon: XCircle },
  };

  const backTo = user?.role === "loading_supervisor" ? "/ops" : user?.role === "accounts" ? "/accounts" : user?.role === "super_admin" ? "/superadmin" : "/admin";

  return (
    <div className="min-h-screen bg-[#0A0A0A]">
      <Navbar />
      <div className="max-w-6xl mx-auto px-6 py-8">
        <Link to={backTo} className="inline-flex items-center gap-2 text-sm text-zinc-400 hover:text-white">
          <ArrowLeft className="w-4 h-4" /> Back
        </Link>

        <div className="mt-6 grid lg:grid-cols-3 gap-4">
          <div className="lg:col-span-2 p-6 bg-[#141414] border border-[#2A2A2A]">
            <div className="flex flex-wrap items-start justify-between gap-3">
              <div>
                <div className="overline text-[#FF5A00]">CN {trip.cn_number}</div>
                <div className="mt-2 flex items-center gap-3 font-mono-ll text-lg">
                  {trip.source_city} <ArrowRight className="w-4 h-4 text-[#FF5A00]" /> {trip.destination_city}
                </div>
              </div>
              <div className="flex items-center gap-2 flex-wrap">
                <StatusBadge status={trip.status === "in_transit" ? "in_transit" : trip.status === "delivered" ? "delivered" : trip.status === "closed" ? "delivered" : trip.status === "approved" ? "confirmed" : trip.status === "loaded" ? "confirmed" : "posted"} />
                <Link to={`/prints/lr/${trip.id}`} target="_blank" rel="noopener noreferrer">
                  <Button size="sm" variant="outline" data-testid="trip-print-lr" className="h-8 rounded-sm border-[#2A2A2A] hover:bg-[#FF5A00]/10 text-[#FF5A00]"><Printer className="w-3 h-3 mr-1" /> LR / POD</Button>
                </Link>
                {canEdit && (
                  <Button size="sm" variant="outline" onClick={openEdit} data-testid="trip-edit-btn"
                    className="h-8 rounded-sm border-[#2A2A2A] hover:bg-sky-500/10 text-sky-400"><Pencil className="w-3 h-3 mr-1" /> Edit</Button>
                )}
                {canDelete && (
                  <Button size="sm" variant="outline" onClick={() => setDelOpen(true)} data-testid="trip-delete-btn"
                    className="h-8 rounded-sm border-[#2A2A2A] hover:bg-rose-500/10 text-rose-400"><Trash2 className="w-3 h-3 mr-1" /> Delete</Button>
                )}
              </div>
            </div>

            <div className="mt-6 grid sm:grid-cols-2 gap-4 text-sm">
              <KV label="Trip type" value={trip.trip_type === "driver_placement" ? "Driver Placement" : "Vehicle Placement"} />
              <KV label="Customer" value={trip.customer?.name} />
              <KV label="Consignor" value={trip.customer?.consignor || "—"} />
              <KV label="Consignee" value={trip.customer?.consignee || "—"} />
              <KV label="Material" value={trip.material || "—"} />
              <KV label="Weight" value={`${trip.weight_kg || 0} kg`} />
              <KV label="SO / Invoice" value={`${trip.sales_order_number || "—"} · ${trip.invoice_number || "—"}`} />
              <KV label="Pickup / Delivery" value={`${trip.pickup_date || "—"} · ${trip.delivery_date || "—"}`} />
              <KV label="Vehicle" value={trip.vehicle?.vehicle_number ? `${trip.vehicle.vehicle_number} (${trip.vehicle.owner || "—"})` : "—"} icon={Truck} />
              <KV label="Driver" value={trip.driver?.name || "—"} icon={User} />
            </div>

            {(canApprove || canAdvance) && isOps && (
              <div className="mt-6 flex gap-3">
                {canApprove && (
                  <Button onClick={approve} data-testid="trip-approve-btn"
                    className="rounded-sm bg-emerald-600 hover:bg-emerald-500 text-black font-bold">
                    <CheckCircle2 className="w-4 h-4 mr-1" /> Approve trip
                  </Button>
                )}
                {canAdvance && (
                  <Button onClick={() => advance(nextStatus)} data-testid="trip-advance-btn"
                    className="rounded-sm bg-[#FF5A00] hover:bg-[#FF7A2C] text-black font-bold">
                    Move to {nextStatus.replace("_", " ").toUpperCase()}
                  </Button>
                )}
              </div>
            )}
          </div>

          <div className="p-6 bg-[#141414] border border-[#2A2A2A]">
            <div className="overline text-zinc-500">Customer freight</div>
            <div className="font-display font-black text-3xl mt-1">{formatINR(trip.money?.customer_freight || 0)}</div>
            <div className="mt-4 grid grid-cols-2 gap-2 text-sm">
              <KV label="Lorry hire" value={formatINR(trip.money?.lorry_hire_total || 0)} />
              <KV label="Lorry advance" value={formatINR(trip.money?.lorry_advance || 0)} />
              <KV label="Lorry balance" value={formatINR(trip.money?.lorry_balance || 0)} />
              <KV label="Driver hire" value={formatINR(trip.money?.driver_hire_total || 0)} />
            </div>
            <div className="mt-4 pt-4 border-t border-[#2A2A2A]">
              <div className="overline text-zinc-500">Margin estimate</div>
              <div className="font-display font-black text-2xl mt-1 text-[#FF5A00]">{formatINR((trip.money?.customer_freight || 0) - (trip.money?.lorry_hire_total || 0) - (trip.money?.driver_hire_total || 0) - totalExpense)}</div>
              <div className="text-xs text-zinc-500 mt-1">Freight − lorry hire − driver hire − expenses</div>
            </div>
          </div>
        </div>

        {/* Expenses + Payments */}
        <div className="mt-6 grid lg:grid-cols-2 gap-4">
          <div className="p-5 bg-[#141414] border border-[#2A2A2A]">
            <div className="overline text-[#FF5A00] mb-3">Trip expenses</div>
            {isOps && (
              <form onSubmit={addExpense} className="flex flex-wrap gap-2 mb-4">
                <Select value={exp.kind} onValueChange={(v) => setExp({ ...exp, kind: v })}>
                  <SelectTrigger className="h-9 w-36 bg-[#0A0A0A] border-[#2A2A2A] rounded-sm focus:ring-[#FF5A00]"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {["fuel","toll","driver_bhatta","loading","unloading","maintenance","detention","other"].map(k => <SelectItem key={k} value={k}>{k.replace("_"," ")}</SelectItem>)}
                  </SelectContent>
                </Select>
                <Input type="number" value={exp.amount} onChange={(e) => setExp({ ...exp, amount: e.target.value })} placeholder="₹" required
                  data-testid="expense-amount-input" className="h-9 w-28 bg-[#0A0A0A] border-[#2A2A2A] rounded-sm focus-visible:ring-[#FF5A00]" />
                <Input value={exp.note} onChange={(e) => setExp({ ...exp, note: e.target.value })} placeholder="note"
                  className="h-9 flex-1 min-w-[100px] bg-[#0A0A0A] border-[#2A2A2A] rounded-sm focus-visible:ring-[#FF5A00]" />
                <Button type="submit" data-testid="add-expense-btn" className="h-9 rounded-sm bg-[#FF5A00] hover:bg-[#FF7A2C] text-black font-bold">Add</Button>
              </form>
            )}
            <div className="text-xs">
              {expenses.length === 0 && <div className="text-zinc-500 py-3">No expenses logged.</div>}
              {expenses.map(x => (
                <div key={x.id} className="flex justify-between py-1 border-b border-[#2A2A2A] last:border-b-0">
                  <div><span className="uppercase tracking-wider text-[#FF5A00] text-[10px] mr-2">{x.kind}</span>{x.note}</div>
                  <div className="font-mono-ll">{formatINR(x.amount)}</div>
                </div>
              ))}
              <div className="flex justify-between mt-3 pt-3 border-t border-[#2A2A2A] font-display font-bold">
                <span>Total</span><span className="text-[#FF5A00]">{formatINR(totalExpense)}</span>
              </div>
            </div>
          </div>

          <div className="p-5 bg-[#141414] border border-[#2A2A2A]">
            <div className="overline text-[#FF5A00] mb-3">Trip payments</div>
            {isAcc && (
              <form onSubmit={addPayment} className="flex flex-wrap gap-2 mb-4">
                <Select value={pay.kind} onValueChange={(v) => setPay({ ...pay, kind: v })}>
                  <SelectTrigger className="h-9 w-40 bg-[#0A0A0A] border-[#2A2A2A] rounded-sm focus:ring-[#FF5A00]"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {["lorry_advance","lorry_balance","driver_payout","customer_payment","refund","other"].map(k => <SelectItem key={k} value={k}>{k.replace("_"," ")}</SelectItem>)}
                  </SelectContent>
                </Select>
                <Input type="number" value={pay.amount} onChange={(e) => setPay({ ...pay, amount: e.target.value })} placeholder="₹" required
                  data-testid="payment-amount-input" className="h-9 w-28 bg-[#0A0A0A] border-[#2A2A2A] rounded-sm focus-visible:ring-[#FF5A00]" />
                <Input value={pay.note} onChange={(e) => setPay({ ...pay, note: e.target.value })} placeholder="note"
                  className="h-9 flex-1 min-w-[100px] bg-[#0A0A0A] border-[#2A2A2A] rounded-sm focus-visible:ring-[#FF5A00]" />
                <Button type="submit" data-testid="add-payment-btn" className="h-9 rounded-sm bg-[#FF5A00] hover:bg-[#FF7A2C] text-black font-bold">Save</Button>
              </form>
            )}
            <div className="text-xs">
              {payments.length === 0 && <div className="text-zinc-500 py-3">No payments recorded.</div>}
              {payments.map(x => (
                <div key={x.id} className="flex justify-between py-1 border-b border-[#2A2A2A] last:border-b-0">
                  <div><span className="uppercase tracking-wider text-[#FF5A00] text-[10px] mr-2">{x.kind}</span>{x.method} · {x.note}</div>
                  <div className="font-mono-ll">{formatINR(x.amount)}</div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* POD (Proof of Delivery) */}
        <div className="mt-6 p-5 bg-[#141414] border border-[#2A2A2A]" data-testid="pod-section">
          <div className="flex items-center justify-between mb-3">
            <div className="overline text-[#FF5A00]">Proof of Delivery (POD) &amp; Trip remark</div>
            {trip.pod && (() => {
              const meta = podOutcomeMeta[trip.pod.outcome] || {};
              const Icon = meta.Icon || CheckCircle2;
              const colorMap = { emerald: "text-emerald-400 border-emerald-500/40 bg-emerald-500/10", amber: "text-amber-400 border-amber-500/40 bg-amber-500/10", rose: "text-rose-400 border-rose-500/40 bg-rose-500/10" };
              return (
                <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 text-xs uppercase font-bold border rounded-sm ${colorMap[meta.color] || ""}`} data-testid="pod-badge">
                  <Icon className="w-3.5 h-3.5" /> {meta.label || trip.pod.outcome}
                </span>
              );
            })()}
          </div>

          {/* POD Photo (uploaded proof) */}
          {isOps && ["in_transit", "delivered", "closed"].includes(trip.status) && (
            <PodPhotoBlock trip={trip} reload={load} />
          )}

          {trip.pod ? (
            <div className="text-sm text-zinc-300 space-y-1">
              {trip.pod.remarks && <div><span className="overline text-zinc-500 text-[10px] mr-2">REMARKS</span>{trip.pod.remarks}</div>}
              {Number(trip.pod.shortage_qty) > 0 && <div><span className="overline text-zinc-500 text-[10px] mr-2">SHORTAGE</span>{trip.pod.shortage_qty} kg</div>}
              {trip.pod.damage_note && <div><span className="overline text-zinc-500 text-[10px] mr-2">DAMAGE</span>{trip.pod.damage_note}</div>}
              <div className="text-xs text-zinc-500 mt-2">Recorded by {trip.pod.recorded_by_name || "—"} · {trip.pod.recorded_at ? new Date(trip.pod.recorded_at).toLocaleString("en-IN") : ""}</div>
            </div>
          ) : canRecordPod ? (
            <form onSubmit={savePod} className="grid sm:grid-cols-2 gap-3">
              <div>
                <Label className="text-xs uppercase tracking-wider text-zinc-400">Outcome</Label>
                <Select value={pod.outcome} onValueChange={(v) => setPod({ ...pod, outcome: v })}>
                  <SelectTrigger data-testid="pod-outcome-select" className="mt-1 bg-[#0A0A0A] border-[#2A2A2A] rounded-sm focus:ring-[#FF5A00]"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="delivered_ok">Delivered OK</SelectItem>
                    <SelectItem value="short_delivery">Short Delivery</SelectItem>
                    <SelectItem value="damaged_delivery">Damaged Delivery</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wider text-zinc-400">Shortage qty (kg, if any)</Label>
                <Input type="number" value={pod.shortage_qty} onChange={(e) => setPod({ ...pod, shortage_qty: e.target.value })}
                  data-testid="pod-shortage-input" className="mt-1 bg-[#0A0A0A] border-[#2A2A2A] rounded-sm focus-visible:ring-[#FF5A00]" />
              </div>
              <div className="sm:col-span-2">
                <Label className="text-xs uppercase tracking-wider text-zinc-400">POD Remarks (internal)</Label>
                <Input value={pod.remarks} onChange={(e) => setPod({ ...pod, remarks: e.target.value })}
                  placeholder="e.g. Unloaded at Gate 4, sealed & signed by consignee"
                  data-testid="pod-remarks-input" className="mt-1 bg-[#0A0A0A] border-[#2A2A2A] rounded-sm focus-visible:ring-[#FF5A00]" />
              </div>
              {pod.outcome === "damaged_delivery" && (
                <div className="sm:col-span-2">
                  <Label className="text-xs uppercase tracking-wider text-zinc-400">Damage note</Label>
                  <Input value={pod.damage_note} onChange={(e) => setPod({ ...pod, damage_note: e.target.value })}
                    data-testid="pod-damage-input" className="mt-1 bg-[#0A0A0A] border-[#2A2A2A] rounded-sm focus-visible:ring-[#FF5A00]" />
                </div>
              )}
              <div className="sm:col-span-2">
                <Button type="submit" data-testid="save-pod-btn" className="rounded-sm bg-[#FF5A00] hover:bg-[#FF7A2C] text-black font-bold">
                  <PackageCheck className="w-4 h-4 mr-1" /> Save POD &amp; close delivery
                </Button>
                <p className="text-[11px] text-zinc-500 mt-2">POD is internal — feeds into driver rating and analytics. Customer sees only the outcome status on tracking page.</p>
              </div>
            </form>
          ) : (
            <div className="text-xs text-zinc-500">POD can be recorded once the trip is in-transit or delivered.</div>
          )}
        </div>
      </div>

      {/* Edit modal */}
      <Dialog open={editOpen} onOpenChange={setEditOpen}>
        <DialogContent className="max-w-2xl bg-[#141414] border-[#2A2A2A] text-white max-h-[90vh] overflow-y-auto" data-testid="trip-edit-dialog">
          <DialogHeader>
            <DialogTitle className="font-display">Edit Trip · CN {trip.cn_number}</DialogTitle>
            <DialogDescription className="text-zinc-400">
              Correct any entry mistakes. Audit entry will be added to the trip history.
            </DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <EditField label="Customer name" k="customer_name" edit={edit} setEdit={setEdit} />
            <EditField label="Customer phone" k="customer_phone" edit={edit} setEdit={setEdit} />
            <EditField label="Customer GSTIN" k="customer_gstin" edit={edit} setEdit={setEdit} />
            <EditField label="Consignor" k="consignor" edit={edit} setEdit={setEdit} />
            <EditField label="Consignee" k="consignee" edit={edit} setEdit={setEdit} />
            <EditField label="Source city" k="source_city" edit={edit} setEdit={setEdit} />
            <EditField label="Destination city" k="destination_city" edit={edit} setEdit={setEdit} />
            <EditField label="Material" k="material" edit={edit} setEdit={setEdit} />
            <EditField label="Weight (kg)" k="weight_kg" edit={edit} setEdit={setEdit} type="number" />
            <EditField label="Pickup date" k="pickup_date" edit={edit} setEdit={setEdit} type="date" />
            <EditField label="Delivery date" k="delivery_date" edit={edit} setEdit={setEdit} type="date" />
            <EditField label="Sales order #" k="sales_order_number" edit={edit} setEdit={setEdit} />
            <EditField label="Customer invoice #" k="invoice_number" edit={edit} setEdit={setEdit} />
            {isAcc && <>
              <EditField label="Customer freight (₹)" k="customer_freight" edit={edit} setEdit={setEdit} type="number" />
              <EditField label="Lorry hire total (₹)" k="lorry_hire_total" edit={edit} setEdit={setEdit} type="number" />
              <EditField label="Lorry advance (₹)" k="lorry_advance" edit={edit} setEdit={setEdit} type="number" />
              <EditField label="Driver hire total (₹)" k="driver_hire_total" edit={edit} setEdit={setEdit} type="number" />
            </>}
            <div className="sm:col-span-2">
              <EditField label="Remarks" k="remarks" edit={edit} setEdit={setEdit} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditOpen(false)} className="rounded-sm border-[#2A2A2A]">Cancel</Button>
            <Button onClick={saveEdit} data-testid="trip-edit-save" className="rounded-sm bg-[#FF5A00] hover:bg-[#FF7A2C] text-black font-bold">Save changes</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete confirm */}
      <AlertDialog open={delOpen} onOpenChange={setDelOpen}>
        <AlertDialogContent className="bg-[#141414] border-[#2A2A2A] text-white" data-testid="trip-delete-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-display">Delete trip CN {trip.cn_number}?</AlertDialogTitle>
            <AlertDialogDescription className="text-zinc-400">
              This LR will be removed from active records. It cannot be undone from the UI
              (soft-delete is retained in audit log for 90 days). Billed trips cannot be deleted.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-sm border-[#2A2A2A] bg-transparent">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDelete} data-testid="trip-delete-confirm"
              className="rounded-sm bg-rose-600 hover:bg-rose-500 text-white font-bold">Delete trip</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

function EditField({ label, k, edit, setEdit, type = "text" }) {
  return (
    <div>
      <Label className="text-[10px] uppercase tracking-widest text-zinc-500">{label}</Label>
      <Input type={type} value={edit[k] ?? ""} onChange={(e) => setEdit({ ...edit, [k]: e.target.value })}
        data-testid={`edit-${k}`}
        className="mt-1 bg-[#0A0A0A] border-[#2A2A2A] rounded-sm focus-visible:ring-[#FF5A00]" />
    </div>
  );
}

function KV({ label, value, icon: I }) {
  return (
    <div className="flex items-start gap-2">
      {I ? <I className="w-4 h-4 text-[#FF5A00] mt-0.5" /> : null}
      <div>
        <div className="overline text-zinc-500 text-[10px]">{label}</div>
        <div className="font-semibold">{value}</div>
      </div>
    </div>
  );
}

function PodPhotoBlock({ trip, reload }) {
  const [busy, setBusy] = useState(false);
  const backend = process.env.REACT_APP_BACKEND_URL;
  const photoUrl = trip.cn_number ? `${backend}/api/pod-photo/${trip.cn_number}?ts=${trip.updated_at || ""}` : null;
  const hasPhoto = !!(trip.pod && trip.pod.photo_path);

  const upload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setBusy(true);
    try {
      const form = new FormData();
      form.append("file", file);
      await api.post(`/trips/${trip.id}/pod/photo`, form, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("POD photo uploaded");
      reload?.();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setBusy(false); e.target.value = ""; }
  };

  return (
    <div className="mb-4 p-4 border border-dashed border-[#2A2A2A] rounded-sm bg-[#0A0A0A]" data-testid="pod-photo-block">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div className="flex-1 min-w-[220px]">
          <div className="overline text-zinc-500 text-[10px]">POD PHOTO (proof of delivery)</div>
          <p className="text-xs text-zinc-400 mt-1 max-w-md">
            Attach a signed LR, delivery note or unloaded-goods picture. Customers see this
            image on the public track page for consignment <span className="text-[#FF5A00] font-mono">{trip.cn_number}</span>.
          </p>
        </div>
        <div className="flex gap-2 items-center">
          <label className={`inline-flex items-center gap-2 px-3 py-2 text-xs uppercase tracking-wider font-bold rounded-sm cursor-pointer border ${busy ? "opacity-50 pointer-events-none" : ""} border-[#FF5A00] bg-[#FF5A00]/10 text-[#FF5A00] hover:bg-[#FF5A00]/20`}>
            <input type="file" accept="image/*,application/pdf" onChange={upload} className="hidden" data-testid="pod-photo-input" />
            {busy ? "Uploading…" : hasPhoto ? "Replace photo" : "Upload POD photo"}
          </label>
        </div>
      </div>
      {hasPhoto && (
        <div className="mt-4">
          <a href={photoUrl} target="_blank" rel="noopener noreferrer">
            <img src={photoUrl} alt="POD" data-testid="pod-photo-preview"
              className="max-h-56 w-auto rounded-sm border border-[#2A2A2A] object-contain bg-white" />
          </a>
          <div className="text-[10px] text-zinc-500 mt-2 font-mono break-all">
            Public URL: /api/pod-photo/{trip.cn_number}
          </div>
        </div>
      )}
    </div>
  );
}
